// BillingDetailsPanel.tsx
// Displays and allows editing of billing address details

import { MutedText, SubTitle, Title } from '../../account/account-settings.styled';
import { Panel } from '../../settings.styled';
import { ActionText } from '../../account/account-settings.styled';
import { SpaceBetweenRow, Column, Divider, DetailLabel } from '../billing.styled';
import { useSettingsContext } from '../../settings-context';
import { useModalContext } from '../../modal-context';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function BillingDetailsPanel() {
  const { billingDetails } = useSettingsContext();
  const { openModal } = useModalContext();

  // Check if user has billing details
  const existBillingDetails = !!(
    billingDetails?.firstName ||
    billingDetails?.lastName ||
    billingDetails?.companyName
  );

  const handleOpenEditModal = () => {
    openModal('edit');
  };

  return (
    <Panel>
      <Column $gap="16px">
        <SpaceBetweenRow>
          <Title>Billing details</Title>
          <ActionText
            style={{ fontSize: '14px' }}
            onClick={handleOpenEditModal}
            aria-label={existBillingDetails ? 'Edit billing details' : 'Add billing details'}
          >
            {existBillingDetails ? 'Edit billing details' : 'Add billing details'}
          </ActionText>
        </SpaceBetweenRow>

        {existBillingDetails && billingDetails && (
          <>
            {billingDetails.firstName && (
              <div>
                <DetailLabel>First name</DetailLabel>
                <SubTitle style={{ lineHeight: '100%' }}>
                  {billingDetails.firstName || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.lastName && (
              <div>
                <DetailLabel>Last name</DetailLabel>
                <SubTitle style={{ lineHeight: '100%' }}>
                  {billingDetails.lastName || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.companyName && (
              <div>
                <DetailLabel>Company name</DetailLabel>
                <SubTitle style={{ lineHeight: '100%' }}>
                  {billingDetails.companyName || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.streetAddress && (
              <div>
                <DetailLabel>Street address</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.streetAddress || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.city && (
              <div>
                <DetailLabel>City</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.city || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.stateProvince && (
              <div>
                <DetailLabel>State / province</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.stateProvince || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.postalCode && (
              <div>
                <DetailLabel>Postal code</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.postalCode || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.country && (
              <div>
                <DetailLabel>Country</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.country || 'Not provided'}
                </SubTitle>
              </div>
            )}

            {billingDetails.vatTaxId && (
              <div>
                <DetailLabel>VAT / tax ID</DetailLabel>
                <SubTitle style={{ lineHeight: 'normal', fontWeight: '400' }}>
                  {billingDetails.vatTaxId || 'Not provided'}
                </SubTitle>
              </div>
            )}

            <Divider />

            <MutedText style={{ fontSize: '12px', lineHeight: 'normal', fontWeight: '400' }}>
              These details appear on your invoices and are used for tax purposes.
            </MutedText>
          </>
        )}
      </Column>
    </Panel>
  );
}
